import { createTRPCReact } from "@trpc/react-query";
import { createTRPCProxyClient, httpBatchLink } from "@trpc/client";
import type { AppRouter } from "../../../server/src/routers/_app";

export const trpc = createTRPCReact<AppRouter>();

// URL du backend SaaS - configurée via variable d'environnement
const getBackendUrl = () => {
  if (import.meta.env.PROD) {
    return import.meta.env.VITE_BACKEND_URL || "https://api.constrigo.fr";
  }
  return import.meta.env.VITE_BACKEND_URL || "http://localhost:3001";
};

/**
 * Reads the constrigo_csrf cookie set by the backend alongside the httpOnly
 * access token. Must be echoed back as X-CSRF-Token on mutations (double-submit).
 */
function readCsrf(): string | null {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(/(?:^|;\s*)constrigo_csrf=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

const buildHeaders = (): Record<string, string> => {
  const headers: Record<string, string> = { "x-constrigo-app": "OPS" };
  const csrf = readCsrf();
  if (csrf) headers["x-csrf-token"] = csrf;
  return headers;
};

const authedFetch: typeof fetch = async (input, options = {}) => {
  try {
    const response = await fetch(input, { ...options, credentials: "include" });
    // 401 → cookies invalides/expirées, on redirige vers login.
    if (response.status === 401) {
      localStorage.removeItem("admin_token");
      localStorage.removeItem("admin_user");
      if (typeof window !== "undefined" && !window.location.pathname.includes("/login")) {
        window.location.href = "/login";
      }
    }
    return response;
  } catch (error) {
    if (error instanceof TypeError && error.message.includes("fetch")) {
      const backendUrl = getBackendUrl();
      throw new Error(
        `Le serveur backend n'est pas accessible à l'adresse ${backendUrl}. ` +
        `Vérifiez que le backend est démarré et que la variable VITE_BACKEND_URL est correctement configurée.`
      );
    }
    throw error;
  }
};

export const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: `${getBackendUrl()}/trpc`,
      headers: buildHeaders,
      fetch: authedFetch,
    }),
  ],
});

// Vanilla imperative client — used by AuthContext for logout etc.
export const trpcVanilla = createTRPCProxyClient<AppRouter>({
  links: [
    httpBatchLink({
      url: `${getBackendUrl()}/trpc`,
      headers: buildHeaders,
      fetch: authedFetch,
    }),
  ],
});
